// app/javascript/controllers/split_flap_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    text: String,
    delay: { type: Number, default: 50 },
    flipDuration: { type: Number, default: 400 },
    autoStart: { type: Boolean, default: true }
  }
  
  static classes = [
    "container",
    "letter", 
    "letterFront",
    "letterBack",
    "letterFlipping"
  ]
  
  connect() {
    // Classes par défaut si non définies
    this.defaultClasses = {
      container: "flex gap-2 justify-center items-center", // Gap augmenté de 1 à 2
      letter: "relative w-8 h-12 bg-slate-800 rounded-md overflow-hidden transform-gpu perspective-1000",
      letterFront: "absolute inset-0 flex items-center justify-center font-mono text-xl font-bold text-white backface-hidden transform-style-preserve-3d",
      letterBack: "absolute inset-0 flex items-center justify-center font-mono text-xl font-bold text-white backface-hidden transform-style-preserve-3d rotateY-180",
      letterFlipping: "animate-flip"
    }

    // État de l'animation
    this.animationState = []
    this.animationFrameId = null

    if (this.autoStartValue) {
      this.renderLetters()
    }
  }
  
  disconnect() {
    // Nettoyer l'animation quand le contrôleur est déconnecté
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  // Méthode publique pour démarrer l'animation
  start() {
    this.renderLetters()
  }
  
  // Méthode publique pour changer le texte
  changeText(newText) {
    this.textValue = newText
    this.renderLetters()
  }
  
  renderLetters() {
    const text = this.textValue.toUpperCase()

    // Annuler l'animation précédente si elle existe
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }

    const container = this.createContainer()

    // Nettoyer le contenu existant
    this.element.innerHTML = ''

    // Hériter de TOUS les styles du parent
    const parentStyles = window.getComputedStyle(this.element)
    container.style.fontSize = parentStyles.fontSize
    container.style.fontFamily = parentStyles.fontFamily
    container.style.fontWeight = parentStyles.fontWeight
    container.style.color = parentStyles.color
    container.style.letterSpacing = parentStyles.letterSpacing
    container.style.lineHeight = parentStyles.lineHeight

    this.element.appendChild(container)

    // Initialiser l'état d'animation pour chaque lettre
    this.animationState = []

    // Créer chaque lettre
    text.split('').forEach((char, index) => {
      const letterElement = this.createLetterElement(char, index, parentStyles)
      container.appendChild(letterElement)

      // Initialiser l'état de cette lettre
      this.animationState.push({
        element: letterElement,
        frontFace: letterElement.querySelector('.split-flap-face.front'),
        backFace: letterElement.querySelector('.split-flap-face.back'),
        targetChar: char,
        index: index,
        startTime: null,
        lastFlipTime: 0,
        currentFlip: 0,
        isFlipping: false,
        completed: char === ' ' // Les espaces sont immédiatement complétés
      })

      // Les espaces n'ont pas d'animation
      if (char === ' ') {
        const state = this.animationState[index]
        state.frontFace.innerHTML = '&nbsp;'
        state.backFace.innerHTML = '&nbsp;'
        letterElement.dispatchEvent(new CustomEvent('letter:complete', {
          detail: { char: char, element: letterElement }
        }))
      }
    })

    // Démarrer la boucle d'animation unique
    this.startAnimationLoop()
  }
  
  createContainer() {
    const container = document.createElement('div')
    container.className = this.getClass('container')
    return container
  }
  
  createLetterElement(targetChar, index, parentStyles) {
    const letterEl = document.createElement('div')
    letterEl.className = this.getClass('letter')
    letterEl.dataset.index = index
    letterEl.dataset.target = targetChar
    
    // Calculer la taille basée sur la police du parent avec plus de largeur
    const fontSize = parseFloat(parentStyles.fontSize)
    
    // Largeurs personnalisées selon le caractère
    let letterWidth
    if (targetChar === ' ') {
      letterWidth = fontSize * 0.4 // Espaces un peu plus larges
    } else if (['I', '1', '!', '.', ',', ':', ';'].includes(targetChar)) {
      letterWidth = fontSize * 0.6 // Caractères étroits
    } else if (['W', 'M'].includes(targetChar)) {
      letterWidth = fontSize * 1.2 // Caractères très larges
    } else {
      letterWidth = fontSize * 0.9 // Largeur par défaut plus généreuse
    }
    
    const letterHeight = fontSize * 1.4
    
    letterEl.style.width = `${letterWidth}px`
    letterEl.style.height = `${letterHeight}px`
    letterEl.style.fontSize = parentStyles.fontSize
    letterEl.style.fontFamily = parentStyles.fontFamily
    letterEl.style.fontWeight = parentStyles.fontWeight
    letterEl.style.minWidth = `${fontSize * 0.6}px` // Largeur minimale
    
    // Appliquer les classes CSS pour l'effet 3D
    letterEl.style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
    letterEl.style.border = '2px solid #2d3748'
    letterEl.style.borderRadius = '8px'
    letterEl.style.boxShadow = `
      inset 0 1px 3px rgba(255, 255, 255, 0.1),
      inset 0 -1px 3px rgba(0, 0, 0, 0.3),
      0 4px 8px rgba(0, 0, 0, 0.3)
    `
    letterEl.style.position = 'relative'
    letterEl.style.overflow = 'hidden'
    letterEl.style.transformStyle = 'preserve-3d'
    letterEl.style.perspective = '1000px'
    
    // Face avant (visible initialement)
    const frontFace = document.createElement('div')
    frontFace.className = 'split-flap-face front'
    frontFace.style.cssText = `
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${parentStyles.color};
      font-family: inherit;
      font-weight: inherit;
      font-size: inherit;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      backface-visibility: hidden;
      transform: rotateY(0deg);
    `
    frontFace.textContent = this.getRandomChar()
    
    // Face arrière (pour l'effet flip)
    const backFace = document.createElement('div')
    backFace.className = 'split-flap-face back'
    backFace.style.cssText = `
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${parentStyles.color};
      font-family: inherit;
      font-weight: inherit;
      font-size: inherit;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      backface-visibility: hidden;
      transform: rotateY(180deg);
    `
    backFace.textContent = this.getRandomChar()
    
    letterEl.appendChild(frontFace)
    letterEl.appendChild(backFace)
    
    return letterEl
  }
  
  startAnimationLoop() {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789?!.,-"
    const flipInterval = 80 // Intervalle entre les flips (ms)
    const flipAnimationDuration = 150 // Durée de l'animation CSS du flip
    const startTime = performance.now()

    const animate = (currentTime) => {
      let allCompleted = true

      this.animationState.forEach((state, index) => {
        // Ignorer les lettres déjà complétées
        if (state.completed) return

        // Calculer le délai de démarrage pour cette lettre
        const letterDelay = index * this.delayValue
        const elapsedTime = currentTime - startTime - letterDelay

        // Cette lettre n'a pas encore commencé
        if (elapsedTime < 0) {
          allCompleted = false
          return
        }

        // Initialiser le temps de démarrage de cette lettre
        if (state.startTime === null) {
          state.startTime = currentTime
          state.lastFlipTime = currentTime
        }

        const letterElapsedTime = currentTime - state.startTime
        const totalDuration = this.flipDurationValue
        const progress = Math.min(letterElapsedTime / totalDuration, 1)

        // L'animation est terminée
        if (progress >= 1) {
          state.frontFace.textContent = state.targetChar
          state.backFace.textContent = state.targetChar
          state.element.classList.remove('flipping')
          state.completed = true

          state.element.dispatchEvent(new CustomEvent('letter:complete', {
            detail: { char: state.targetChar, element: state.element }
          }))
          return
        }

        allCompleted = false

        // Temps écoulé depuis le dernier flip
        const timeSinceLastFlip = currentTime - state.lastFlipTime

        // C'est le moment de flipper ?
        if (timeSinceLastFlip >= flipInterval && !state.isFlipping) {
          state.isFlipping = true
          state.lastFlipTime = currentTime
          state.currentFlip++

          // Calculer la probabilité de montrer la bonne lettre
          let shouldShowTarget = false
          if (progress > 0.6) {
            const finalPhaseProgress = (progress - 0.6) / 0.4
            shouldShowTarget = Math.random() < finalPhaseProgress * 0.8
          }
          if (progress > 0.9) {
            shouldShowTarget = true
          }

          // Choisir le caractère à afficher
          const nextChar = shouldShowTarget ? state.targetChar : this.getRandomChar(alphabet)

          // Démarrer le flip
          state.element.classList.add('flipping')
          state.backFace.textContent = nextChar

          // Après l'animation flip, échanger les faces
          setTimeout(() => {
            state.frontFace.textContent = nextChar
            state.isFlipping = false
            state.element.classList.remove('flipping')

            // Si on a affiché la bonne lettre et qu'on est dans la phase finale, marquer comme complété
            if (nextChar === state.targetChar && progress > 0.8) {
              state.completed = true
              state.element.dispatchEvent(new CustomEvent('letter:complete', {
                detail: { char: state.targetChar, element: state.element }
              }))
            }
          }, flipAnimationDuration)
        }
      })

      // Continuer l'animation si toutes les lettres ne sont pas complétées
      if (!allCompleted) {
        this.animationFrameId = requestAnimationFrame(animate)
      } else {
        this.animationFrameId = null
      }
    }

    // Démarrer la boucle d'animation
    this.animationFrameId = requestAnimationFrame(animate)
  }
  
  getRandomChar(alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789") {
    return alphabet[Math.floor(Math.random() * alphabet.length)]
  }
  
  getClass(className) {
    // Utilise les classes personnalisées si définies, sinon les classes par défaut
    if (this.hasClass(className)) {
      return this[`${className}Class`]
    }
    return this.defaultClasses[className] || ''
  }
  
  hasClass(className) {
    return this[`has${className.charAt(0).toUpperCase() + className.slice(1)}Class`]
  }
  
  // Méthodes utilitaires publiques
  reset() {
    this.element.innerHTML = ''
  }
  
  pause() {
    // TODO: Implémenter la pause des animations
  }
  
  resume() {
    // TODO: Implémenter la reprise des animations
  }
}